// Rate limiting service for chat protection
export interface SessionLimits {
  messagesPerSession: number;
  messagesPerMinute: number;
  sessionDuration: number; // in milliseconds
}

export interface RateLimitResult {
  allowed: boolean;
  reason?: string;
  remainingMessages?: number;
  resetTime?: number;
}

interface SessionData {
  messageCount: number;
  lastMessage: number;
  recentMessages: number[];
  createdAt: number;
}

class RateLimitService {
  private sessions = new Map<string, SessionData>();
  private limits: SessionLimits;

  constructor() {
    // Get limits from environment variables with defaults
    this.limits = {
      messagesPerSession: parseInt(import.meta.env.VITE_CHAT_MAX_MESSAGES_PER_SESSION) || 50,
      messagesPerMinute: parseInt(import.meta.env.VITE_CHAT_MAX_MESSAGES_PER_MINUTE) || 5,
      sessionDuration: parseInt(import.meta.env.VITE_CHAT_SESSION_DURATION) || 30 * 60 * 1000 // 30 minutes
    };
  }

  checkLimit(sessionId: string): RateLimitResult {
    const now = Date.now();
    let session = this.sessions.get(sessionId);

    // Create new session if doesn't exist
    if (!session) {
      session = {
        messageCount: 0,
        lastMessage: 0,
        recentMessages: [],
        createdAt: now
      };
      this.sessions.set(sessionId, session);
    }

    // Check session expiry
    if (now - session.createdAt > this.limits.sessionDuration) {
      this.sessions.delete(sessionId);
      return { 
        allowed: false, 
        reason: 'Session expired. Please refresh the page to start a new chat session.' 
      };
    }

    // Check total messages per session
    if (session.messageCount >= this.limits.messagesPerSession) {
      return { 
        allowed: false, 
        reason: `You've reached the maximum of ${this.limits.messagesPerSession} messages per session. Please refresh the page to start a new session.`,
        remainingMessages: 0
      };
    }

    // Clean up old messages (older than 1 minute)
    session.recentMessages = session.recentMessages.filter(
      time => now - time < 60000
    );

    // Check rate limiting (messages per minute)
    if (session.recentMessages.length >= this.limits.messagesPerMinute) {
      const oldestMessage = Math.min(...session.recentMessages);
      const resetTime = oldestMessage + 60000;
      
      return { 
        allowed: false, 
        reason: `Rate limit exceeded. You can send ${this.limits.messagesPerMinute} messages per minute. Please wait a moment.`,
        resetTime
      };
    }

    // Calculate remaining messages
    const remainingInSession = this.limits.messagesPerSession - session.messageCount;
    const remainingInMinute = this.limits.messagesPerMinute - session.recentMessages.length;

    return { 
      allowed: true,
      remainingMessages: Math.min(remainingInSession, remainingInMinute)
    };
  }

  recordMessage(sessionId: string): void {
    const now = Date.now();
    const session = this.sessions.get(sessionId);
    
    if (session) {
      session.messageCount++;
      session.lastMessage = now;
      session.recentMessages.push(now);
      
      // Clean up old messages
      session.recentMessages = session.recentMessages.filter(
        time => now - time < 60000
      );
    }
  }

  getSessionStats(sessionId: string): { messageCount: number; remainingMessages: number } | null {
    const session = this.sessions.get(sessionId);
    if (!session) return null;

    const now = Date.now();
    
    // Clean up old messages
    session.recentMessages = session.recentMessages.filter(
      time => now - time < 60000
    );

    const remainingInSession = this.limits.messagesPerSession - session.messageCount;
    const remainingInMinute = this.limits.messagesPerMinute - session.recentMessages.length;

    return {
      messageCount: session.messageCount,
      remainingMessages: Math.min(remainingInSession, remainingInMinute)
    };
  }

  // Clean up expired sessions periodically
  cleanup(): void {
    const now = Date.now();
    for (const [sessionId, session] of this.sessions.entries()) {
      if (now - session.createdAt > this.limits.sessionDuration) {
        this.sessions.delete(sessionId);
      }
    }
  }

  // Reset session (useful for testing or manual reset)
  resetSession(sessionId: string): void {
    this.sessions.delete(sessionId);
  }

  // Get current limits (useful for displaying to user)
  getLimits(): SessionLimits {
    return { ...this.limits };
  }
}

// Export singleton instance
export const rateLimitService = new RateLimitService();

// Clean up expired sessions every 5 minutes
setInterval(() => {
  rateLimitService.cleanup();
}, 5 * 60 * 1000);
